import type {ReactNode} from 'react';
import {useEffect, useMemo, useRef} from 'react';
import {QRCode} from 'antd';
import type {PaymentSession} from '../types';
import {useCountdownDisplay} from '../hooks/useCountdownDisplay';
import iconAlipay from '../../assets/icon-alipay.png';
import iconWechatPay from '../../assets/icon-wechat-pay.png';
import {QRMask} from './QRMask';
import type {QRMaskStatus} from './QRMask';
import styles from './PaymentPanel.module.less';
import type {PayMethod} from '@/services/commerce/entities/payment';
import {fenToYuan} from '@/utils/money';
import {createCx} from '@/utils/cx';

const cx = createCx(styles);

/** PaymentPanel 可发出的事件类型 */
export interface PaymentPanelEvent {
    // 二维码重试展示
    type: 'retry-show'; // 如果需要新增UI态事件，可以继续追加
}

/** PaymentPanel 的 props */
interface PaymentPanelProps {
    session: PaymentSession;
    /** 二维码尺寸（px），默认 200 */
    qrSize?: number;
    /** 底部协议区 */
    agreement?: ReactNode;
    /** 自定义类名 */
    className?: string;
    /** 组件内部事件通知（不含任何埋点逻辑，由业务方响应） */
    onEvent?: (event: PaymentPanelEvent) => void;
}

/** 支付方式 → 图标映射 */
const PAY_METHOD_ICONS: Record<PayMethod, string> = {
    Alipay: iconAlipay,
    WeChatPay: iconWechatPay,
};

/** 二维码内边距（容器内白边） */
const QR_PADDING = 8;

/**
 * 支付面板组件
 * 展示「金额 + 二维码 + 倒计时 + 支付方式 + 协议」
 * 不含关闭按钮和二次确认逻辑（由业务弹窗层控制）
 */
// eslint-disable-next-line complexity
export function PaymentPanel({
    session,
    qrSize = 200,
    agreement,
    className,
    onEvent,
}: PaymentPanelProps) {
    const {state, refreshQR} = session;
    const onEventRef = useRef(onEvent);
    onEventRef.current = onEvent;

    // 从 order 派生展示数据
    const order = 'order' in state ? state.order : null;
    const payAmount = order ? fenToYuan(order.payAmount) : '--';
    const payload = order?.qrCode.payload ?? '';
    const expireAt = order?.qrCode.expireAt ?? null;
    const payMethods = order?.qrCode.payMethods ?? [];

    // 倒计时展示
    const countdown = useCountdownDisplay(expireAt);

    // 蒙层状态派生
    const maskStatus: QRMaskStatus = useMemo(() => {
        switch (state.status) {
            case 'idle':
            case 'creating':
            case 'qr-loading':
                return 'loading';
            case 'qr-expired':
                return 'expired';
            case 'failed':
                return 'failed';
            case 'qr-ready':
            case 'success':
                return null;
            default:
                return null;
        }
    }, [state.status]);

    // 状态变为 expired/failed 时通知业务方（重试态展现）
    const retryVisible = maskStatus === 'expired' || maskStatus === 'failed';
    const prevRetryVisibleRef = useRef(false);
    useEffect(() => {
        if (retryVisible && !prevRetryVisibleRef.current) {
            onEventRef.current?.({type: 'retry-show'});
        }
        prevRetryVisibleRef.current = retryVisible;
    }, [retryVisible]);

    return (
        <section className={cx('panel', className)}>
            {/* 顶部：支付金额 */}
            <header className={styles.amount}>
                <span className={styles.label}>支付金额</span>
                <span className={styles.price}>
                    <em className={styles.currency}>¥</em>
                    {payAmount}
                </span>
            </header>

            {/* 中部：二维码容器 */}
            <div className={styles.qrContainer}>
                <div style={{width: qrSize, padding: QR_PADDING}}>
                    <QRCode
                        value={payload || 'placeholder'}
                        size={qrSize - QR_PADDING * 2}
                        type="svg"
                        bordered={false}
                        marginSize={0}
                        status="active"
                    />
                </div>
                <span className={styles.countdown}>
                    {countdown ? `请在 ${countdown} 内完成支付` : '\u00A0'}
                </span>
                <QRMask
                    status={maskStatus}
                    reason={state.status === 'failed' ? state.reason : undefined}
                    onAction={refreshQR}
                />
            </div>

            {/* 底部：支付方式 + 协议 */}
            <footer className={styles.footer}>
                <div className={styles.methods}>
                    <span className={styles.methodsLabel}>支持支付方式：</span>
                    <div className={styles.methodsIcons}>
                        {payMethods.length > 0 &&
                            payMethods.map((method) => (
                                <img
                                    key={method}
                                    src={PAY_METHOD_ICONS[method]}
                                    alt={method}
                                    className={styles.methodIcon}
                                />
                            ))}
                        {!payMethods.length && (
                            <img
                                src={PAY_METHOD_ICONS.Alipay}
                                alt="Alipay"
                                className={styles.methodIcon}
                            />
                        )}
                    </div>
                </div>
                {agreement && <div className={styles.agreement}>{agreement}</div>}
            </footer>
        </section>
    );
}
